/**
 * JWT utility functions for parsing and checking token expiry
 */

interface JWTPayload {
	iat?: number; // issued at
	exp?: number; // expiry
	aud?: string; // audience
	iss?: string; // issuer
	sub?: string; // subject (user id)
	email?: string;
	email_verified?: boolean;
	[key: string]: any;
}

/**
 * Decodes a JWT token without verification (client-side only)
 * @param token - The JWT token to decode
 * @returns The decoded payload or null if invalid
 */
export function decodeJWT(token: string): JWTPayload | null {
	try {
		// Split the token into parts
		const parts = token.split(".");
		if (parts.length !== 3) {
			return null;
		}

		// Decode the payload (second part)
		const payload = parts[1];
		// Add padding if needed
		const paddedPayload = payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), "=");

		// Decode base64url
		const decodedPayload = atob(paddedPayload.replace(/-/g, "+").replace(/_/g, "/"));

		return JSON.parse(decodedPayload);
	} catch (error) {
		console.error("Error decoding JWT:", error);
		return null;
	}
}

/**
 * Checks if a JWT token is expired
 * @param token - The JWT token to check
 * @returns true if expired, false if still valid, null if token is invalid
 */
export function isTokenExpired(token: string): boolean | null {
	const payload = decodeJWT(token);
	if (!payload || !payload.exp) {
		return null;
	}

	const currentTime = Math.floor(Date.now() / 1000);
	return payload.exp < currentTime;
}

/**
 * Checks if a JWT token will expire soon (within the specified threshold)
 * @param token - The JWT token to check
 * @param thresholdMinutes - Number of minutes before expiry to consider "expiring soon" (default: 5)
 * @returns true if expiring soon, false if not, null if token is invalid
 */
export function isTokenExpiringSoon(token: string, thresholdMinutes: number = 5): boolean | null {
	const payload = decodeJWT(token);
	if (!payload || !payload.exp) {
		return null;
	}

	const currentTime = Math.floor(Date.now() / 1000);
	const thresholdTime = currentTime + thresholdMinutes * 60;

	return payload.exp < thresholdTime && payload.exp > currentTime;
}

/**
 * Gets the expiry time of a JWT token
 * @param token - The JWT token to check
 * @returns Date object of expiry time, or null if token is invalid
 */
export function getTokenExpiryTime(token: string): Date | null {
	const payload = decodeJWT(token);
	if (!payload || !payload.exp) {
		return null;
	}

	return new Date(payload.exp * 1000);
}

/**
 * Gets the time remaining until token expiry
 * @param token - The JWT token to check
 * @returns object with time remaining or null if token is invalid
 */
export function getTimeUntilExpiry(token: string): {
	minutes: number;
	seconds: number;
	totalSeconds: number;
} | null {
	const payload = decodeJWT(token);
	if (!payload || !payload.exp) {
		return null;
	}

	const currentTime = Math.floor(Date.now() / 1000);
	const timeRemaining = payload.exp - currentTime;

	if (timeRemaining <= 0) {
		return { minutes: 0, seconds: 0, totalSeconds: 0 };
	}

	const minutes = Math.floor(timeRemaining / 60);
	const seconds = timeRemaining % 60;

	return {
		minutes,
		seconds,
		totalSeconds: timeRemaining,
	};
}
